import { Injectable, inject } from '@angular/core';
import { OrderEditStore, OrderEditState } from './order-edit.store';
import { OrderEditQuery } from './order-edit.query';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { OutboundOrder } from 'src/app/core/models/order/outboundOrder';
import { Subscription } from 'rxjs';

/**
 * Order Edit Persistence Service
 *
 * Auto-saves order draft to localStorage to prevent data loss when:
 * - User switches tabs/apps on mobile
 * - Browser is killed by OS to save memory
 * - Browser crashes or is force-closed
 *
 * This ensures work-in-progress orders survive browser restarts.
 * 
 * Uses orderId-specific localStorage keys to prevent cross-contamination
 * between different orders.
 */
@Injectable({ providedIn: 'root' })
export class OrderEditPersistenceService {
  private store = inject(OrderEditStore);
  private query = inject(OrderEditQuery);

  private readonly STORAGE_KEY_PREFIX = 'procurement_order_draft_';
  private readonly AUTO_SAVE_DEBOUNCE_MS = 2000; // Save 2 seconds after last change
  private readonly MAX_DRAFT_AGE_DAYS = 7;
  
  private currentOrderId: string | null = null; // Track current order ID
  private autoSaveSubscription?: Subscription; // Track subscription for cleanup

  /**
   * Generate storage key for specific order
   */
  private getStorageKey(orderId: string): string {
    return `${this.STORAGE_KEY_PREFIX}${orderId}`;
  }

  /**
   * Start auto-save listener for a specific order
   * Call this when the order edit component initializes
   */
  startAutoSave(orderId: string): void {
    console.log('💾 [OrderEditPersistence] Starting auto-save for order:', orderId);
    
    // Stop previous auto-save if any
    this.stopAutoSave();
    
    this.currentOrderId = orderId;

    // Watch for changes to currentOrder and auto-save
    this.autoSaveSubscription = this.query
      .select((state) => state.currentOrder)
      .pipe(
        filter((order) => order !== null && order.id === orderId), // Only save matching order
        debounceTime(this.AUTO_SAVE_DEBOUNCE_MS), // Wait for user to stop typing
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)) // Only save if changed
      )
      .subscribe((order) => {
        if (order && order.articles && order.articles.length > 0) {
          this.saveToLocalStorage(order);
        }
      });
  }

  /**
   * Stop auto-save listener
   */
  stopAutoSave(): void {
    if (this.autoSaveSubscription) {
      this.autoSaveSubscription.unsubscribe();
      this.autoSaveSubscription = undefined;
      console.log('🛑 [OrderEditPersistence] Auto-save stopped');
    }
  }

  /**
   * Save order to localStorage with orderId-specific key
   */
  private saveToLocalStorage(order: OutboundOrder): void {
    try {
      if (!order.id) {
        console.warn('⚠️ [OrderEditPersistence] Cannot save order without ID');
        return;
      }

      const stateToSave = {
        order: order.toFirestore(),
        savedAt: new Date().toISOString(),
        orderId: order.id,
        supplierOrgId: order.supplier?.id || null, // For debugging
      };

      const storageKey = this.getStorageKey(order.id);
      localStorage.setItem(storageKey, JSON.stringify(stateToSave));
      console.log('💾 [OrderEditPersistence] Auto-saved draft to localStorage:', storageKey);
    } catch (error) {
      console.error('❌ [OrderEditPersistence] Failed to save to localStorage:', error);
    }
  }

  /**
   * Try to restore order from localStorage for a specific orderId
   * Returns the restored order if found and valid, null otherwise
   * 
   * IMPORTANT: This should ONLY be called when editing an EXISTING order
   * Never call this when creating a new order (orderId = 'new-order')
   */
  tryRestoreFromLocalStorage(orderId: string): OutboundOrder | null {
    try {
      // GUARD: Never restore for new orders
      if (!orderId || orderId === 'new-order') {
        console.log('💾 [OrderEditPersistence] Skipping restore for new order');
        return null;
      }

      const storageKey = this.getStorageKey(orderId);
      const savedData = localStorage.getItem(storageKey);

      if (!savedData) {
        console.log('💾 [OrderEditPersistence] No saved draft found for:', storageKey);
        return null;
      }

      const parsed = JSON.parse(savedData);

      // Validate orderId matches
      if (parsed.orderId !== orderId) {
        console.warn('⚠️ [OrderEditPersistence] OrderId mismatch, clearing:', storageKey);
        this.clearLocalStorage(orderId);
        return null;
      }

      // Check if the draft is not too old
      const savedAt = new Date(parsed.savedAt);
      const daysSinceSave = (Date.now() - savedAt.getTime()) / (1000 * 60 * 60 * 24);

      if (daysSinceSave > this.MAX_DRAFT_AGE_DAYS) {
        console.log('💾 [OrderEditPersistence] Saved draft is too old, clearing:', storageKey);
        this.clearLocalStorage(orderId);
        return null;
      }

      // Restore the order
      const restoredOrder = new OutboundOrder(parsed.order);
      console.log('✅ [OrderEditPersistence] Restored draft from localStorage:', storageKey);

      return restoredOrder;
    } catch (error) {
      console.error('❌ [OrderEditPersistence] Failed to restore from localStorage:', error);
      return null;
    }
  }

  /**
   * Clear the saved draft from localStorage for a specific order
   */
  clearLocalStorage(orderId?: string): void {
    try {
      const targetOrderId = orderId || this.currentOrderId;
      
      if (!targetOrderId) {
        console.warn('⚠️ [OrderEditPersistence] No orderId to clear');
        return;
      }

      const storageKey = this.getStorageKey(targetOrderId);
      localStorage.removeItem(storageKey);
      console.log('🧹 [OrderEditPersistence] Cleared localStorage draft:', storageKey);
    } catch (error) {
      console.error('❌ [OrderEditPersistence] Failed to clear localStorage:', error);
    }
  }

  /**
   * Check if there's a saved draft for a specific order
   */
  hasSavedDraft(orderId: string): boolean {
    if (!orderId || orderId === 'new-order') return false;
    const storageKey = this.getStorageKey(orderId);
    return !!localStorage.getItem(storageKey);
  }

  /**
   * Get info about the saved draft for a specific order
   */
  getSavedDraftInfo(orderId: string): { orderId: string; savedAt: Date; supplierOrgId: string | null } | null {
    try {
      if (!orderId || orderId === 'new-order') return null;
      
      const storageKey = this.getStorageKey(orderId);
      const savedData = localStorage.getItem(storageKey);
      if (!savedData) return null;

      const parsed = JSON.parse(savedData);
      return {
        orderId: parsed.orderId,
        savedAt: new Date(parsed.savedAt),
        supplierOrgId: parsed.supplierOrgId,
      };
    } catch {
      return null;
    }
  }

  /**
   * Cleanup old drafts from localStorage (older than MAX_DRAFT_AGE_DAYS)
   * Call this periodically or on app init
   */
  cleanupOldDrafts(): void {
    try {
      const keysToRemove: string[] = [];
      
      // Iterate through all localStorage keys
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        
        if (key && key.startsWith(this.STORAGE_KEY_PREFIX)) {
          try {
            const data = localStorage.getItem(key);
            if (data) {
              const parsed = JSON.parse(data);
              const savedAt = new Date(parsed.savedAt);
              const daysSinceSave = (Date.now() - savedAt.getTime()) / (1000 * 60 * 60 * 24);
              
              if (daysSinceSave > this.MAX_DRAFT_AGE_DAYS) {
                keysToRemove.push(key);
              }
            }
          } catch (e) {
            // Invalid data, mark for removal
            keysToRemove.push(key);
          }
        }
      }
      
      // Remove old drafts
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        console.log('🧹 [OrderEditPersistence] Cleaned up old draft:', key);
      });
      
      if (keysToRemove.length > 0) {
        console.log(`✅ [OrderEditPersistence] Cleaned up ${keysToRemove.length} old drafts`);
      }
    } catch (error) {
      console.error('❌ [OrderEditPersistence] Failed to cleanup old drafts:', error);
    }
  }
}

